/**
 * Маркери подій на графіках (реліз гри, перше потрапляння в топ-селлери тощо).
 * Стиль як у SteamDB: пігулки з короткою міткою та тултіпом.
 */

export type ChartEventMarkerType =
  | "release"
  | "ea"
  | "topsellers"
  | "wishlist";

export interface ChartEventMarker {
  timestampMs: number;
  /** Коротка мітка на маркері (наприклад "RL", "TS") */
  label: string;
  /** Повний текст для тултіпа (наприклад "Release", "First in Top Sellers") */
  tooltipLabel: string;
  type: ChartEventMarkerType;
}

/** Steam сейл або фестиваль — відображається як напівпрозора смуга на графіку */
export interface ChartFestivalMarker {
  startMs: number;
  endMs: number;
  name: string;
  url?: string | null;
}

const FESTIVAL_COLORS = [
  "#beee11",
  "#66c0f4",
  "#f49b14",
  "#b46cf4",
];

export type FestivalScatterPoint = {
  value: [number, number];
  itemStyle: { color: string; borderColor: string; borderWidth: number };
  festivalName: string;
  festivalStart: string;
  festivalEnd: string;
  festivalUrl: string | null | undefined;
};

/**
 * Повертає два markLine-рядки на кожен фестиваль (start + end),
 * кожен — вертикальна лінія від низу до верху з label і hover tooltip.
 * markLine з `type: "min"` не залежить від zoom — правильно поводиться завжди.
 */
export function buildFestivalScatterSeries(
  festivals: ChartFestivalMarker[],
  chartData: [number, number][],
  isMobile: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any | null {
  if (!festivals.length || !chartData.length) return null;

  const sorted = [...chartData].sort((a, b) => a[0] - b[0]);
  const xMin = sorted[0][0];
  const xMax = sorted[sorted.length - 1][0];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markLineData: any[] = [];

  for (let i = 0; i < festivals.length; i++) {
    const f = festivals[i];
    const color = FESTIVAL_COLORS[i % FESTIVAL_COLORS.length];
    const startDate = new Date(f.startMs).toLocaleDateString("en-US", { dateStyle: "medium" });
    const endDate = new Date(f.endMs).toLocaleDateString("en-US", { dateStyle: "medium" });
    const tooltip = `${f.name}||${startDate} – ${endDate}||${f.url ?? ""}`;

    if (f.startMs >= xMin && f.startMs <= xMax) {
      markLineData.push({
        xAxis: f.startMs,
        name: tooltip,
        lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.7 },
        label: {
          show: true,
          formatter: (p: { name: string }) => p.name.split("||")[0],
          position: "insideStartTop",
          color,
          fontSize: isMobile ? 8 : 9,
          backgroundColor: "rgba(14,20,27,0.75)",
          padding: [2, 5],
          borderRadius: 3,
          borderWidth: 1,
          borderColor: color,
        },
        emphasis: {
          lineStyle: { opacity: 1, width: 2 },
        },
      });
    }
    if (f.endMs >= xMin && f.endMs <= xMax) {
      markLineData.push({
        xAxis: f.endMs,
        name: tooltip,
        lineStyle: { color, width: 1, type: "dashed", opacity: 0.4 },
        label: { show: false },
        emphasis: {
          lineStyle: { opacity: 0.8, width: 1.5 },
        },
      });
    }
  }

  if (!markLineData.length) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tooltipFormatter = (params: any) => {
    const parts = (params.name as string).split("||");
    const name = parts[0];
    const dates = parts[1];
    const url = parts[2];
    return `
      <div style="font-weight:600;margin-bottom:4px;">${name}</div>
      <div style="color:#8f98a0;font-size:11px;">${dates}</div>
      ${url ? '<div style="color:#66c0f4;font-size:11px;margin-top:4px;">Click to open on Steam ↗</div>' : ""}
    `;
  };

  const tooltipConfig = {
    show: true,
    trigger: "item" as const,
    backgroundColor: "#11161d",
    borderColor: "#3a4a5a",
    borderWidth: 1,
    padding: [6, 10] as [number, number],
    textStyle: { color: "#cfd8e3", fontSize: 12 },
    formatter: tooltipFormatter,
  };

  return {
    type: "line",
    name: "_festivals",
    data: [],
    silent: false,
    cursor: "pointer",
    markLine: {
      symbol: ["none", "none"],
      animation: false,
      silent: false,
      tooltip: tooltipConfig,
      data: markLineData,
    },
  };
}

/**
 * Парсить дату релізу в мілісекунди (для coord на time axis).
 */
export function parseReleaseDateToMs(
  releaseDate: string | null | undefined
): number | null {
  if (!releaseDate) return null;
  const date = new Date(releaseDate);
  if (!Number.isFinite(date.getTime())) return null;
  const timestamp = parseInt(releaseDate, 10);
  if (!Number.isNaN(timestamp) && timestamp > 0) {
    return timestamp <= 1e12 ? timestamp * 1000 : timestamp;
  }
  return date.getTime();
}

/**
 * Повертає Y-значення на лінії для заданого часу (найближча точка або інтерполяція).
 */
export function getYAtTime(
  chartData: [number, number][],
  timeMs: number
): number | null {
  if (chartData.length === 0) return null;
  if (chartData.length === 1) return chartData[0][1];

  const sorted = [...chartData].sort((a, b) => a[0] - b[0]);
  let prev = sorted[0];
  let next = sorted[sorted.length - 1];

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i][0] >= timeMs) {
      next = sorted[i];
      prev = i > 0 ? sorted[i - 1] : sorted[i];
      break;
    }
    prev = sorted[i];
  }

  if (prev[0] === next[0]) return prev[1];
  const t = (timeMs - prev[0]) / (next[0] - prev[0]);
  return prev[1] + t * (next[1] - prev[1]);
}

/** Повертає кінець того ж дня (23:59:59.999) у часовому поясі користувача. */
function endOfDayLocal(ms: number): number {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

/**
 * Збирає маркери для графіка: Early Access, реліз, опційно "перше в топ-селлерах".
 *
 * Точність маркера:
 *  - якщо передано epoch (releaseTs / eaReleaseTs з steam_app_details.release_ts_utc) —
 *    використовуємо exact-моменtт анлоку (для модерних релізів з date_full точність до секунди)
 *  - інакше fallback на текстову дату + округлення до кінця дня (для старих ігор / placeholder)
 */
export function buildChartEventMarkers(options: {
  releaseDate?: string | null;
  eaReleaseDate?: string | null;
  releaseTs?: number | null;
  eaReleaseTs?: number | null;
  firstTopSellersTs?: number | null;
  chartType?: "followers" | "topsellers" | "wishlist" | "ccu" | "reviews" | "price";
}): ChartEventMarker[] {
  const markers: ChartEventMarker[] = [];

  // Epoch path is exact; text path is rounded to end-of-day local.
  const releaseMs =
    options.releaseTs != null
      ? options.releaseTs * 1000
      : (() => {
          const ms = parseReleaseDateToMs(options.releaseDate);
          return ms != null ? endOfDayLocal(ms) : null;
        })();
  const eaReleaseMs =
    options.eaReleaseTs != null
      ? options.eaReleaseTs * 1000
      : (() => {
          const ms = parseReleaseDateToMs(options.eaReleaseDate);
          return ms != null ? endOfDayLocal(ms) : null;
        })();

  if (eaReleaseMs != null) {
    markers.push({
      timestampMs: eaReleaseMs,
      label: "EA",
      tooltipLabel: "Early Access",
      type: "ea",
    });
  }

  if (releaseMs != null) {
    markers.push({
      timestampMs: releaseMs,
      label: "RL",
      tooltipLabel: "Release",
      type: "release",
    });
  }

  // Маркер "перше потрапляння в топ-селлери" — має сенс на графіку топ-селлерів, якщо є дата до релізу
  const firstTs = options.firstTopSellersTs;
  if (
    firstTs != null &&
    options.chartType === "topsellers" &&
    releaseMs != null &&
    firstTs * 1000 < releaseMs
  ) {
    markers.push({
      timestampMs: firstTs * 1000,
      label: "TS",
      tooltipLabel: "First in Top Sellers",
      type: "topsellers",
    });
  }

  return markers.sort((a, b) => a.timestampMs - b.timestampMs);
}
